import { apiClient } from './api';
import { CrmImportJob } from './crmImportApi';

// Google Sheets lead import (google-sheets-lead-import Phase 1).
// Backend: gsheet_import_routes.py — /api/crm-import/gsheets/*, gated by the
// `integrations` module. A Sheet tab is staged server-side as a CSV and comes
// back as an ordinary CrmImportJob (source_kind 'gsheet'), so everything after
// the import call reuses the existing CRM import flow unchanged.

export type GsheetConnectionStatus = 'disconnected' | 'connected' | 'needs_reauth';

export interface GsheetStatus {
  connected: boolean;
  status: GsheetConnectionStatus;
  google_email: string | null;
  scopes: string[];
  picker_available: boolean;
}

export interface GsheetPickerConfig {
  api_key: string;
  app_id: string;
  access_token: string;
  expires_at: string | null;
}

export interface GsheetTab {
  gid: number;
  title: string;
  index: number;
  row_count_estimate: number | null;
}

export interface GsheetTabsResponse {
  spreadsheet_id: string;
  title: string;
  tabs: GsheetTab[];
}

// The /import response is exactly the /upload job shape. `existing_source_id`
// arrives with saved syncing sources (Phase 3a); absent means null.
export type GsheetImportJob = CrmImportJob & { existing_source_id?: number | null };

// ── Phase 3a: saved syncing sources ──────────────────────────────────────────
export type GsheetSourceStatus =
  | 'active'
  | 'paused'
  | 'needs_reauth'
  | 'needs_remap'
  | 'rekey_suspected'
  | 'source_lost'
  | 'error';

export interface GsheetSource {
  id: number;
  spreadsheet_id: string;
  spreadsheet_title: string | null;
  sheet_gid: number;
  sheet_title: string | null;
  target_module: string;
  campaign_id: string | null;
  campaign_name: string | null;
  project_id: string | null;
  key_strategy: 'column' | 'email_created' | 'row_hash';
  key_column: string | null;
  key_time_column: string | null;
  key_row_fields: string[];
  sync_enabled: boolean;
  sync_interval_minutes: number;
  next_sync_at: string | null;
  last_synced_at: string | null;
  last_job_id: number | null;
  status: GsheetSourceStatus;
  status_reason: string | null;
  rows_recognised_last: number | null;
  consecutive_failures: number;
  last_failure_reason: string | null;
  last_failure_at: string | null;
  last_run_warnings: number;        // Phase 3b — always 0 for now
  key_conflict_rows: number;
  rows_imported_total: number;
  rows_deferred_over_cap: number;   // Phase 3b — always 0 for now
  rows_failed_total: number;
  rows_invalid: number;
  rows_pending_retry: number;
  created_at: string | null;
}

export const gsheetImportApi = {
  status: () => apiClient.get<GsheetStatus>('/crm-import/gsheets/status'),
  authStart: () => apiClient.get<{ auth_url: string }>('/crm-import/gsheets/auth'),
  pickerConfig: () => apiClient.get<GsheetPickerConfig>('/crm-import/gsheets/picker-config'),
  listTabs: (spreadsheetId: string) =>
    apiClient.get<GsheetTabsResponse>(
      `/crm-import/gsheets/spreadsheets/${encodeURIComponent(spreadsheetId)}/tabs`,
    ),
  importTab: (spreadsheetId: string, sheetGid: number) =>
    apiClient.post<GsheetImportJob>('/crm-import/gsheets/import', {
      spreadsheet_id: spreadsheetId,
      sheet_gid: sheetGid,
    }),
  disconnect: () =>
    apiClient.delete<{ disconnected: boolean; revoke_url: string; sources_paused: number }>(
      '/crm-import/gsheets/connection',
    ),
  // Phase 3a — saved syncing sources
  listSources: () => apiClient.get<GsheetSource[]>('/crm-import/gsheets/sources'),
  setSyncEnabled: (id: number, syncEnabled: boolean) =>
    apiClient.patch<GsheetSource>(`/crm-import/gsheets/sources/${id}`, { sync_enabled: syncEnabled }),
  // crm-per-project-scoping: pick the CRM project for a source that has none yet.
  setProject: (id: number, projectId: string) =>
    apiClient.patch<GsheetSource>(`/crm-import/gsheets/sources/${id}`, { project_id: projectId }),
  resume: (id: number, confirmNewRows = false) =>
    apiClient.post<GsheetSource>(
      `/crm-import/gsheets/sources/${id}/resume`,
      confirmNewRows ? { confirm_new_rows: true } : {},
    ),
  retryFailed: (id: number) =>
    apiClient.post<{ requeued: number; next_sync_at: string | null }>(
      `/crm-import/gsheets/sources/${id}/retry-failed`,
    ),
  remap: (id: number) => apiClient.post<GsheetImportJob>(`/crm-import/gsheets/sources/${id}/remap`),
  deleteSource: (id: number) => apiClient.delete<{ deleted: boolean }>(`/crm-import/gsheets/sources/${id}`),
};

// Fixed vocabulary from the OAuth callback (?gsheets_error=<code>). Never
// render the raw query value — unknown codes fall back to a generic message.
export const GSHEETS_CALLBACK_ERRORS: Record<string, string> = {
  missing_params: "Google didn't return the expected details — please try connecting again.",
  invalid_state: 'That Google sign-in link expired — please try connecting again.',
  token_exchange: "Couldn't finish connecting Google Sheets — please try again.",
  access_denied: 'Google Sheets access was not granted.',
  oauth_error: 'Google reported a problem connecting Google Sheets — please try again.',
};
export const GSHEETS_CALLBACK_FALLBACK = "Couldn't connect Google Sheets";
